import moment, { Moment } from "moment-timezone";
import { TierItem } from "../typings/pricing";
import { ScheduleItem, getScheduleItemRange, getUpcomingNcaaTournamentStart } from "./ScheduleService";
import { getProductDuration } from "./SubscriptionService";
import { getFreeTrialClaimed } from "./UserService";
import { createContext, useContext, useEffect, useState } from "react";
import { DEFAULT_TIMEZONE } from "src/constants";

const BEFORE_DATE_RANGE: number = 3;
const AFTER_DATE_RANGE: number = 0;

export const getSportChangeRange = (tier: TierItem) => {
  const today = moment();
  const endDate = moment();
  const trialEndDate = endDate.add(3, "days");

  if (getFreeTrialClaimed()) {
    return `Renews ${getProductDuration(tier.key)}ly at $${tier.price}`; // prettier-ignore
  }
  return `Free from ${today.format("MMMM D YYYY")} to \
${trialEndDate.format("MMMM D YYYY")}, \
then $${tier.price}/${tier.durationDisplayAbv}`;
};

export const getStartDateValue = (startDate: string) => {
  return moment.utc(startDate).valueOf();
};

/** The time the page was rendered, from its RENDERED_AT_PROP_NAME prop. Provided by _app. */
export const RenderedAtContext = createContext<number | undefined>(undefined);

let pageHydrated = false;

/** Called by _app once the first page has hydrated. */
export const setPageHydrated = () => {
  pageHydrated = true;
};

/**
 * The current time and the time zone to show dates in.
 *
 * Static pages can be served hours after they were rendered. So while the first page
 * hydrates, this returns the time the page was rendered, in DEFAULT_TIMEZONE, the same
 * values the server used. After that it returns the current time in the browser's time zone.
 */
export const useClock = (): { now: Moment; timezone: string } => {
  const renderedAt = useContext(RenderedAtContext);
  const [hydrating, setHydrating] = useState(!pageHydrated);
  useEffect(() => {
    setHydrating(false);
  }, []);
  if (hydrating) {
    return { now: moment.tz(renderedAt ?? Date.now(), DEFAULT_TIMEZONE), timezone: DEFAULT_TIMEZONE };
  }
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return { now: moment.tz(timezone), timezone };
};

export const useStartDateTime = (startDate: string, showToday?: boolean) => {
  const { now: today, timezone } = useClock();
  if (!startDate) {
    return "--";
  }
  const startTime = moment.tz(startDate, timezone);
  if (startTime.isSame(today, "day")) {
    return showToday ? `Today, ${startTime.format("h:mm A")}` : startTime.format("h:mm A");
  }
  let tomorrow = today.clone().add(1, "d");
  if (startTime.isSame(tomorrow, "day")) {
    return `Tomorrow, ${startTime.format("h:mm A")}`;
  }
  let yesterday = today.clone().subtract(1, "d");
  if (startTime.isSame(yesterday, "day")) {
    return `Yesterday, ${startTime.format("h:mm A")}`;
  }
  return startTime.format("ddd M/D, h:mm A");
};

export const useStartDate = (startDate: Moment) => {
  const { now } = useClock();
  const today = now.clone().startOf("day");
  if (startDate.isSame(today, "day")) {
    return "Today";
  }
  const tomorrow = now.clone().add(1, "d").startOf("day");
  if (startDate.isSame(tomorrow, "day")) {
    return "Tomorrow";
  }
  const yesterday = now.clone().subtract(1, "d").startOf("day");
  if (startDate.isSame(yesterday, "day")) {
    return "Yesterday";
  }
  return startDate.format("ddd M/D");
};

export const useLocalDate = (date: string, format: string = "MMM D, YYYY, h:mma z"): string => {
  const { timezone } = useClock();
  return moment.tz(date, timezone).format(format);
};

const getFirstStart = (schedule: ScheduleItem[]) => getScheduleItemRange(schedule[0]).after;
const getLastEnd = (schedule: ScheduleItem[]) => getScheduleItemRange(schedule[schedule.length - 1]).before;

/** Finds the first entry that isn't skipped and is either the current or a future one */
const findCurrentOrUpcomingPeriod = (schedule: ScheduleItem[], now: Moment = moment()) =>
  schedule.find((period) => {
    const { after, before } = getScheduleItemRange(period);
    return !period.skip && (now.isBetween(after, before, undefined, "[]") || after.isAfter(now));
  });

export const getScheduleIndex = (schedule: ScheduleItem[], now: Moment = moment()): number => {
  if (!schedule || schedule.length < 1) {
    return 0;
  }
  if (getFirstStart(schedule).isAfter(now)) {
    // Season hasn't started yet, find the first schedule entry that
    // we can use if the we need to skip preseason
    const index = schedule.findIndex((period) => !period.skip);
    // Default to 0 if we don't find a valid index
    return index === -1 ? 0 : index;
  } else if (getLastEnd(schedule).isBefore(now)) {
    // Season is over, use the last index
    return schedule.length - 1;
  } else {
    const period = findCurrentOrUpcomingPeriod(schedule, now);
    // Default to 0 if we don't find a valid index
    return period ? schedule.indexOf(period) : 0;
  }
};

export const getScheduleStartDate = (schedule: ScheduleItem[]): Moment | undefined => {
  if (!schedule || schedule.length < 1) {
    return;
  }
  if (getFirstStart(schedule).isAfter()) {
    const selectedPeriod = schedule.find((period) => !period.skip);
    return selectedPeriod && getScheduleItemRange(selectedPeriod).after;
  } else if (getLastEnd(schedule).isBefore()) {
    return getScheduleItemRange(schedule[schedule.length - 1]).after;
  }
  const week = findCurrentOrUpcomingPeriod(schedule);
  return week && getScheduleItemRange(week).after;
};

export const getScheduleEndDate = (schedule: ScheduleItem[]): Moment | undefined => {
  if (!schedule || schedule.length < 1) {
    return;
  }
  if (getFirstStart(schedule).isAfter()) {
    const selectedPeriod = schedule.find((period) => !period.skip);
    return selectedPeriod && getScheduleItemRange(selectedPeriod).before;
  } else if (getLastEnd(schedule).isBefore()) {
    return getLastEnd(schedule);
  }
  const week = findCurrentOrUpcomingPeriod(schedule);
  return week && getScheduleItemRange(week).before;
};

/**
 * Get the default before date range of the given league for the home page. This
 * method temporarily resolves two issues: 1) NFL/NCAAF has a weekly schedule vs daily on
 * other sports and 2) We want to show upcoming games when not in season. E.G: NFL games in June
 *
 * These are temporary fixes until we can get gql that can query for events with limit variable.
 *
 * @param league The name of the league we're searching for
 */
export const getDefaultBeforeDateRange = (league: string, schedule: ScheduleItem[]) => {
  if (league) {
    const lowerLeague = league.toLowerCase();
    switch (lowerLeague) {
      case "nfl":
      // TODO add this for the superbowl
      // return moment.tz("2022-02-14", DEFAULT_TIMEZONE).tz("UTC");
      // eslint-disable-next-line no-fallthrough
      case "ncaaf":
      case "deb":
      case "epl":
      case "esp":
      case "itsa":
      case "ucl":
      case "mls":
        const endDate = getScheduleEndDate(schedule);
        if (endDate) {
          return endDate;
        }
    }
  }

  return moment().tz(DEFAULT_TIMEZONE).add(BEFORE_DATE_RANGE, "day").endOf("day").utc();
};

/**
 * Get the default after date range of the given league for the home page. This
 * method temporarily resolves two issues: 1) NFL/NCAAF has a weekly schedule vs daily on
 * other sports and 2) We want to show upcoming games when not in season. E.G: NFL games in June
 *
 * These are temporary fixes until we can get gql that can query for events with limit variable.
 * @param league The name of the league we're searching for
 */
export const getDefaultAfterDateRange = (league: string, schedule: ScheduleItem[]) => {
  if (league) {
    const lowerLeague = league.toLowerCase();
    switch (lowerLeague) {
      case "nfl":
      // TODO add this for the superbowl
      // return moment.tz("2022-02-06", DEFAULT_TIMEZONE).tz("UTC");
      // eslint-disable-next-line no-fallthrough
      case "ncaaf":
      case "deb":
      case "epl":
      case "esp":
      case "itsa":
      case "ucl":
      case "mls":
        const startDate = getScheduleStartDate(schedule);
        if (startDate) {
          return startDate;
        }
    }
  }

  return moment().tz(DEFAULT_TIMEZONE).subtract(AFTER_DATE_RANGE, "day").startOf("day").utc();
};

/**
 * Get the default selected date of the given league for the games page. This
 * method temporarily resolves two issues: 1) NFL/NCAAF has a weekly schedule vs daily on
 * other sports and 2) We want to show upcoming games when not in season. E.G: NFL games in June
 *
 * These are temporary fixes until we can get gql that can query for events with limit variable.
 * @param sportKey The name of the league we're searching for
 */
export const getDefaultSelectedDate = (sportKey: string | string[] | undefined, now: Moment = moment()) => {
  if (sportKey === "mlb") {
    // TODO add this for MLB start
    const mlbSpringEnd = moment("2023-03-29");
    const mlbRegStart = moment("2023-03-30");
    if (mlbSpringEnd.isBefore(now) && mlbRegStart.isAfter(now)) {
      return mlbRegStart;
    }
  }
  if (sportKey === "ncaab") {
    const tournamentStart = getUpcomingNcaaTournamentStart();
    if (tournamentStart) {
      return tournamentStart;
    }
  }
  // if (sportKey === "nfl") {
  //   // TODO update this for the Superbowl.
  //   const nflDate = moment.tz("2022-02-13", DEFAULT_TIMEZONE);
  //   if (nflDate.isAfter()) {
  //     return nflDate;
  //   }
  // }
  return now.clone().tz(DEFAULT_TIMEZONE);
};

export const sortByDate = (eventA: any, eventB: any) => {
  const startMomentA = moment(eventA.startDate);
  const startMomentB = moment(eventB.startDate);
  if (startMomentA.isBefore(startMomentB)) {
    return -1;
  }
  if (startMomentA.isAfter(startMomentB)) {
    return 1;
  }
  return 0;
};
